// src/components/sidebar/components/Brand.js
// ENHANCED: Modern logo animations, gradient effects, improved visual hierarchy
// Features: Animated logo entrance, pulsing effects, enhanced separators
//
// --- UPDATE (Ops AI status) -----------------------------------------------------
//   • Optional `status` prop: { level: 'ok' | 'warn' | 'critical' | 'idle', label, detail }.
//     The logo glow and a small status pill follow it (green → amber → red), so the
//     sidebar shows at a glance whether FlowSense AI sees anything wrong (fed by Content.js
//     from the SLA AI overview + FlowSense Notify). Without the prop it looks as before.
//   • Respects "reduce motion" (no spin / pulse for users who turned animations off).
//   • useColorModeValue calls hoisted (one was inside JSX); unused imports removed.
// --- END UPDATE -----------------------------------------------------------------

import React, { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Chakra imports
import {
  Flex,
  useColorModeValue,
  Box,
  Text,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { MdWaterDrop } from "react-icons/md";

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionFlex = motion(Flex);
const MotionBox = motion(Box);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const logoContainerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      staggerChildren: 0.2,
    }
  }
};

const logoVariants = {
  hidden: { scale: 0, rotate: -180 },
  visible: {
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
    }
  }
};

const textVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    }
  }
};

// ============================================================================
// PULSING ANIMATION KEYFRAMES
// --- UPDATE: the glow colour comes from a CSS variable so it can follow the AI status
// ============================================================================
const pulseKeyframes = `
  @keyframes fs-pulse-glow {
    0%, 100% { box-shadow: 0 0 20px var(--fs-glow, rgba(66, 153, 225, 0.4)); }
    50%      { box-shadow: 0 0 30px var(--fs-glow-strong, rgba(66, 153, 225, 0.6)); }
  }
  @keyframes fs-status-ping {
    0%   { transform: scale(1);   opacity: .7; }
    80%, 100% { transform: scale(2.4); opacity: 0; }
  }
`;

// --- UPDATE: status palette
const STATUS = {
  ok:       { scheme: 'green',  glow: 'rgba(56, 161, 105, 0.35)', strong: 'rgba(56, 161, 105, 0.55)', dot: 'green.400',  label: 'All systems normal' },
  warn:     { scheme: 'orange', glow: 'rgba(221, 107, 32, 0.40)', strong: 'rgba(221, 107, 32, 0.65)', dot: 'orange.400', label: 'Needs attention' },
  critical: { scheme: 'red',    glow: 'rgba(229, 62, 62, 0.45)',  strong: 'rgba(229, 62, 62, 0.75)',  dot: 'red.500',    label: 'Action required' },
  idle:     { scheme: 'blue',   glow: 'rgba(66, 153, 225, 0.4)',  strong: 'rgba(66, 153, 225, 0.6)',  dot: 'blue.400',   label: 'Smart Monitoring' },
};
// --- END UPDATE

export function SidebarBrand({ status }) {
  const reduceMotion = useReducedMotion();
  const s = STATUS[status?.level] || STATUS.idle;
  const showStatus = Boolean(status && status.level && status.level !== 'idle');

  // ============================================================================
  // ENHANCED THEME COLORS (all hoisted)
  // ============================================================================
  const brandSubtextColor = useColorModeValue("gray.600", "gray.400");

  const brandGradient = useColorModeValue(
    "linear(to-r, blue.500, purple.600)",
    "linear(to-r, blue.400, purple.500)"
  );

  const iconBg = useColorModeValue(
    "linear-gradient(135deg, #4299E1 0%, #805AD5 100%)",
    "linear-gradient(135deg, #63B3ED 0%, #9F7AEA 100%)"
  );

  const iconShadow = useColorModeValue(
    "0 4px 14px 0 rgba(66, 153, 225, 0.3)",
    "0 4px 14px 0 rgba(99, 179, 237, 0.4)"
  );

  const glowBg = useColorModeValue(
    "radial(circle, blue.100, transparent)",
    "radial(circle, blue.900, transparent)"
  );

  const pillBg = useColorModeValue(`${s.scheme}.50`, 'whiteAlpha.100');
  const pillColor = useColorModeValue(`${s.scheme}.700`, `${s.scheme}.200`);
  const pillBorder = useColorModeValue(`${s.scheme}.200`, `${s.scheme}.700`);
  const tooltipBg = useColorModeValue("gray.700", "gray.300");
  const tooltipColor = useColorModeValue("white", "gray.800");

  return (
    <MotionFlex
      align="center"
      direction="column"
      variants={logoContainerVariants}
      initial={reduceMotion ? false : "hidden"}
      animate="visible"
      position="relative"
      zIndex={1}
    >
      <style>{pulseKeyframes}</style>

      {/* Logo Container with animated icon */}
      <MotionFlex
        variants={logoVariants}
        align="center"
        justify="center"
        direction="column"
        my="28px"
        gap={3}
      >
        {/* Animated Icon Circle */}
        <MotionBox
          whileHover={reduceMotion ? undefined : {
            scale: 1.1,
            rotate: 360,
            transition: { duration: 0.6, ease: "easeInOut" }
          }}
          whileTap={reduceMotion ? undefined : { scale: 0.95 }}
          position="relative"
        >
          <Flex
            align="center"
            justify="center"
            w="60px"
            h="60px"
            borderRadius="2xl"
            bgGradient={iconBg}
            boxShadow={iconShadow}
            animation={reduceMotion ? undefined : "fs-pulse-glow 3s infinite"}
            sx={{ '--fs-glow': s.glow, '--fs-glow-strong': s.strong }}
            position="relative"
            cursor="pointer"
            aria-hidden="true"
            _before={{
              content: '""',
              position: "absolute",
              top: "-2px",
              left: "-2px",
              right: "-2px",
              bottom: "-2px",
              borderRadius: "2xl",
              bgGradient: brandGradient,
              opacity: 0.3,
              filter: "blur(8px)",
              zIndex: -1,
            }}
          >
            <Icon
              as={MdWaterDrop}
              boxSize={8}
              color="white"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
            />
          </Flex>

          {/* --- UPDATE: status dot on the logo */}
          {showStatus && (
            <Box position="absolute" top="-3px" right="-3px" w="14px" h="14px" pointerEvents="none">
              {!reduceMotion && s !== STATUS.ok && (
                <Box position="absolute" inset={0} borderRadius="full" bg={s.dot} animation="fs-status-ping 1.6s cubic-bezier(0,0,.2,1) infinite" />
              )}
              <Box position="absolute" inset={0} borderRadius="full" bg={s.dot} borderWidth="2px" borderColor="white" />
            </Box>
          )}
          {/* --- END UPDATE */}
        </MotionBox>

        {/* Brand Text */}
        <MotionBox variants={textVariants}>
          <Flex direction="column" align="center" gap={1}>
            <Text
              fontSize="2xl"
              fontWeight="800"
              bgGradient={brandGradient}
              bgClip="text"
              letterSpacing="tight"
              lineHeight="1"
            >
              FlowSense
            </Text>

            {/* --- UPDATE: subtitle becomes the AI status pill when there is a status */}
            {showStatus ? (
              <Tooltip label={status.detail || status.label || s.label} hasArrow placement="bottom" bg={tooltipBg} color={tooltipColor} isDisabled={!status.detail}>
                <Flex
                  align="center"
                  gap={1.5}
                  px={2.5}
                  py={0.5}
                  mt={1}
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor={pillBorder}
                  bg={pillBg}
                  role="status"
                  aria-live="polite"
                >
                  <Box w="6px" h="6px" borderRadius="full" bg={s.dot} />
                  <Text fontSize="10px" fontWeight="700" color={pillColor} letterSpacing="wide" textTransform="uppercase" noOfLines={1}>
                    {status.label || s.label}
                  </Text>
                </Flex>
              </Tooltip>
            ) : (
              <Text
                fontSize="xs"
                color={brandSubtextColor}
                fontWeight="600"
                letterSpacing="wide"
                textTransform="uppercase"
              >
                Smart Monitoring
              </Text>
            )}
            {/* --- END UPDATE */}
          </Flex>
        </MotionBox>
      </MotionFlex>

      {/* Enhanced Separator with gradient */}
      <Box w="100%" px={4} mb="16px">
        <Box
          h="2px"
          bgGradient={brandGradient}
          borderRadius="full"
          opacity={0.6}
          position="relative"
          _before={{
            content: '""',
            position: "absolute",
            top: "-1px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            height: "4px",
            bgGradient: brandGradient,
            filter: "blur(4px)",
            opacity: 0.4,
          }}
        />
      </Box>

      {/* Decorative glow effect */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width="150px"
        height="150px"
        bgGradient={glowBg}
        borderRadius="full"
        filter="blur(40px)"
        opacity={0.3}
        pointerEvents="none"
        zIndex={0}
      />
    </MotionFlex>
  );
}

const MemoSidebarBrand = memo(SidebarBrand);
export default MemoSidebarBrand;
